import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {MapaTeclas} from '@/components/bateria/cola/mapa-teclas'
import {ValidacionResultado} from '@/components/bateria/validacion-resultado'
import {PorQue} from '@/components/ui/por-que'
import type {ValidacionMasiva} from '@/hooks/bateria/use-bateria-cola'
import {
  familiasMedidas,
  pctDeRatio,
  type CalibracionMotor,
  type SeccionClave,
  type TarjetaCola,
} from '@/lib/bateria-cola'

/** v2 §1.6. El gesto de bloque solo existe en ficha: en marcas, aceptar lo que el lector no vio es fabricar datos. */
export function CierreSeccion({
  seccion,
  siguiente,
  decididas,
  dejadas,
  pendientesDeBloque,
  totalSiguiente,
  onVolverADejadas,
  onAceptarBloque,
  onContinuar,
}: {
  seccion: SeccionClave
  siguiente: SeccionClave | null
  decididas: number
  dejadas: number
  pendientesDeBloque: TarjetaCola[]
  totalSiguiente: number
  onVolverADejadas: () => void
  onAceptarBloque: () => void
  onContinuar: () => void
}) {
  const {t} = useTranslation()
  const ofreceBloque = (seccion === 'confirmar' || seccion === 'escribir') && pendientesDeBloque.length > 0

  return (
    <section className="surface" data-testid="cola-cierre-seccion">
      <header className="surface-divider space-y-1 border-b px-5 py-4">
        <h2 className="flex items-center gap-2 font-heading text-lg text-foreground">
          <span
            aria-hidden="true"
            className="tone-green inline-flex size-5 items-center justify-center rounded-full text-[13px]">
            ✓
          </span>
          {t('admin.bateria.cola.cierre.titulo', {seccion: t(`admin.bateria.cola.seccion.${seccion}`)})}
        </h2>
        <p className="text-sm text-muted-foreground" data-testid="cola-cierre-conteo">
          {t('admin.bateria.cola.cierre.conteo', {decididas, dejadas})}
        </p>
      </header>

      <div className="space-y-4 px-5 py-4">
        {dejadas > 0 && (
          <div className="notice-amber flex flex-wrap items-center justify-between gap-2 rounded-lg px-4 py-3 text-sm">
            <span data-testid="cola-cierre-dejadas">{t('admin.bateria.cola.cierre.dejadas', {count: dejadas})}</span>
            <Button size="sm" variant="outline" data-testid="cola-cierre-volver" onClick={onVolverADejadas}>
              {t('admin.bateria.cola.cierre.volver', {count: dejadas})}
            </Button>
          </div>
        )}

        {ofreceBloque && (
          <div className="rounded-lg border border-dashed border-border p-4" data-testid="cola-cierre-bloque">
            <p className="text-sm text-foreground">
              {t('admin.bateria.cola.cierre.bloqueIntro', {count: pendientesDeBloque.length})}
            </p>
            <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto" data-testid="cola-cierre-bloque-lista">
              {pendientesDeBloque.map((tarjeta) => (
                <li key={tarjeta.ref} className="font-mono text-[11px] text-muted-foreground">
                  {[tarjeta.persona_corta, tarjeta.campo, tarjeta.valor_propuesto].filter(Boolean).join(' · ')}
                </li>
              ))}
            </ul>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" data-testid="cola-cierre-una-por-una" onClick={onVolverADejadas}>
                {t('admin.bateria.cola.cierre.unaPorUna')}
              </Button>
              <Button size="sm" variant="secondary" data-testid="cola-cierre-aceptar-bloque" onClick={onAceptarBloque}>
                {t('admin.bateria.cola.cierre.aceptarBloque', {count: pendientesDeBloque.length})}
              </Button>
            </div>
          </div>
        )}

        {siguiente && (
          <div className="space-y-3" data-testid="cola-cierre-siguiente">
            <p className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">
              {t('admin.bateria.cola.cierre.loQueSigue', {
                seccion: t(`admin.bateria.cola.seccion.${siguiente}`),
                count: totalSiguiente,
              })}
            </p>
            <MapaTeclas seccion={siguiente} />
          </div>
        )}
      </div>

      <div className="surface-divider flex items-center justify-end border-t px-5 py-3">
        <Button className="min-h-11 md:min-h-0" data-testid="cola-cierre-continuar" onClick={onContinuar}>
          {t('admin.bateria.cola.cierre.continuar')}
        </Button>
      </div>
    </section>
  )
}

/**
 * Precisión y recall se pintan por separado y con su `n`; la precisión es sobre lo revisado y subestima.
 * Interno, de calibración: nunca sale en el informe del cliente.
 */
function PrecisionDelMotor({calibracion}: {calibracion: CalibracionMotor | null}) {
  const {t} = useTranslation()
  const pct = pctDeRatio(calibracion?.precision)
  if (calibracion === null || pct === null) return null
  const familias = familiasMedidas(calibracion)
  const recall = pctDeRatio(calibracion.recall)
  return (
    <div className="space-y-0.5 text-xs text-muted-foreground" data-testid="cola-lote-calibracion">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-mono tabular-nums" data-testid="cola-lote-precision">
          {t('admin.bateria.cola.lote.precision', {
            aciertos: calibracion.confirmadas,
            total: calibracion.comparables,
            pct,
          })}
        </span>
        {familias.length > 1 && (
          <span className="font-mono tabular-nums" data-testid="cola-lote-precision-familias">
            {familias
              .map((f) =>
                t('admin.bateria.cola.lote.precisionFamilia', {
                  familia: t(`admin.bateria.cola.familia.${f.familia}`),
                  pct: f.pct,
                })
              )
              .join(' · ')}
          </span>
        )}
        <PorQue
          label={t('admin.bateria.cola.lote.comoSeCalcula')}
          ariaLabel={t('admin.bateria.cola.lote.comoSeCalculaAria', {
            dato: t('admin.bateria.cola.lote.precisionLabel'),
          })}
          testId="cola-lote-precision-ayuda"
          contenidoTestId="cola-lote-precision-ayuda-contenido">
          {t('admin.bateria.cola.lote.precisionAyuda')}
        </PorQue>
      </p>
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-mono tabular-nums" data-testid="cola-lote-recall">
          {recall === null
            ? t('admin.bateria.cola.lote.recallSinDatos')
            : t('admin.bateria.cola.lote.recall', {
                enDuda: calibracion.corregidas_en_duda,
                total: calibracion.corregidas_en_duda + calibracion.corregidas_en_firme,
                firmes: calibracion.firmes_revisadas,
                pct: recall,
              })}
        </span>
        <PorQue
          label={t('admin.bateria.cola.lote.comoSeCalcula')}
          ariaLabel={t('admin.bateria.cola.lote.comoSeCalculaAria', {dato: t('admin.bateria.cola.lote.recallLabel')})}
          testId="cola-lote-recall-ayuda"
          contenidoTestId="cola-lote-recall-ayuda-contenido">
          {t('admin.bateria.cola.lote.recallAyuda')}
        </PorQue>
      </p>
      {calibracion.no_comparables > 0 && (
        <p className="font-mono tabular-nums" data-testid="cola-lote-no-comparables">
          {t('admin.bateria.cola.lote.noComparables', {count: calibracion.no_comparables})}
        </p>
      )}
    </div>
  )
}

/** G5. El servidor encadena el informe al validar; esta pantalla solo lee la respuesta. */
export function CierreLote({
  decididas,
  dejadas,
  sinRecorte,
  calibracion,
  paquetesValidables,
  onValidar,
  validando,
  resultado,
  nombreDePaquete,
  segundaVuelta,
  onSegundaVuelta,
  pendientesDelLote,
  volverA,
}: {
  decididas: number
  dejadas: number
  sinRecorte: number
  calibracion: CalibracionMotor | null
  paquetesValidables: number
  onValidar: () => void
  validando: boolean
  resultado: ValidacionMasiva | null
  nombreDePaquete: (paqueteId: string) => string
  segundaVuelta: number
  onSegundaVuelta: () => void
  /** Del resumen, que se refresca con cada envío; el conteo por persona del detalle ya envejeció. */
  pendientesDelLote: number
  volverA: string
}) {
  const {t} = useTranslation()

  return (
    <section className="surface" data-testid="cola-cierre-lote">
      <header className="surface-divider space-y-1 border-b px-5 py-4">
        <h2 className="font-heading text-lg text-foreground">{t('admin.bateria.cola.lote.titulo')}</h2>
        <p className="text-sm text-muted-foreground" data-testid="cola-lote-resumen">
          {t('admin.bateria.cola.lote.resumen', {decididas, dejadas})}
        </p>
        {sinRecorte > 0 && (
          <p className="text-xs text-muted-foreground" data-testid="cola-lote-sin-recorte">
            {t('admin.bateria.cola.lote.sinRecorte', {count: sinRecorte})}
          </p>
        )}
        <PrecisionDelMotor calibracion={calibracion} />
      </header>

      <div className="space-y-4 px-5 py-4">
        {segundaVuelta > 0 && (
          <div className="notice-amber flex flex-wrap items-center justify-between gap-2 rounded-lg px-4 py-3 text-sm">
            <span data-testid="cola-segunda-vuelta-aviso">
              {t('admin.bateria.cola.lote.segundaVueltaIntro', {count: segundaVuelta})}
            </span>
            <Button size="sm" variant="outline" data-testid="cola-segunda-vuelta" onClick={onSegundaVuelta}>
              {t('admin.bateria.cola.lote.segundaVuelta', {count: segundaVuelta})}
            </Button>
          </div>
        )}

        {resultado && (
          <ValidacionResultado
            resultado={resultado}
            nombreDePaquete={nombreDePaquete}
            arreglarHref={`${volverA}?tab=personas`}
            accionFaltanDudas={
              <Button size="sm" variant="outline" data-testid="cola-informe-seguir" onClick={onSegundaVuelta}>
                {t('admin.bateria.validacion.seguirRevisando')}
              </Button>
            }
          />
        )}
      </div>

      <div className="surface-divider flex flex-wrap items-center justify-between gap-2 border-t px-5 py-3">
        <Link
          to={volverA}
          className="font-mono text-[11px] text-primary hover:underline"
          data-testid="cola-lote-volver">
          {t('admin.bateria.cola.lote.volverAlLote')}
        </Link>
        {pendientesDelLote > 0 && !resultado && (
          <span className="text-xs text-muted-foreground" data-testid="cola-lote-validar-con-dudas">
            {t('admin.bateria.cola.lote.validarConDudas', {pendientes: pendientesDelLote})}
          </span>
        )}
        {resultado && resultado.validados > 0 ? (
          <Button asChild className="min-h-11 md:min-h-0" data-testid="cola-ver-informe">
            <Link to={`${volverA}/informe`}>{t('admin.bateria.cola.lote.verInforme')}</Link>
          </Button>
        ) : (
          <Button
            className="min-h-11 md:min-h-0"
            data-testid="cola-validar-lote"
            disabled={validando || paquetesValidables === 0}
            onClick={onValidar}>
            {t('admin.bateria.cola.lote.validar', {count: paquetesValidables})}
          </Button>
        )}
      </div>
    </section>
  )
}
